export const BOOT_SPLASH_REMOVED_EVENT = 'multaqa:boot-splash-removed'
export const TARGET_WAIT_MS = 1500
export const SMOOTH_FRAME_MS = 25
export const SMOOTH_FRAMES = 3
export const QUIET_WAIT_MS = 300
export const REPLAY_LAND_WAIT_MS = 3000

type BootTargetRect = Pick<DOMRect, 'left' | 'top' | 'width' | 'height'>

interface BootSplashController {
  prepare?: () => void
  ready: (target: BootTargetRect | null) => void
  replay: (fromRect: BootTargetRect) => Promise<void>
}

interface PendingBootReady {
  target: BootTargetRect | null
}

interface RouteResolutionSource {
  state: { matches: ReadonlyArray<{ routeId: string; status: string }> }
  subscribe: (event: 'onResolved', listener: () => void) => () => void
}

declare global {
  interface Window {
    __multaqaBoot?: BootSplashController
    __multaqaBootPending?: PendingBootReady
  }
}

export function isBootSplashPresent(): boolean {
  return document.getElementById('multaqa-boot-splash') !== null
}

export function subscribeToBootSplashRemoval(listener: () => void): () => void {
  window.addEventListener(BOOT_SPLASH_REMOVED_EVENT, listener)
  return () => window.removeEventListener(BOOT_SPLASH_REMOVED_EVENT, listener)
}

/**
 * Tells the splash where the M lands as soon as the signed-in layout has
 * passed its guard and the rail logo is on screen. The screen's own data keeps
 * loading behind its skeleton; the splash does not wait for the full route.
 */
export function handoffAfterFirstRoute(router: RouteResolutionSource): void {
  let frame: number | undefined
  let targetTimer: number | undefined
  let quietTimer: number | undefined
  let targetDeadline: number | undefined
  let stopped = false
  let unsubscribeFromRouter: (() => void) | undefined
  const unsubscribe = () => {
    unsubscribeFromRouter?.()
    unsubscribeFromRouter = undefined
  }
  const stop = () => {
    stopped = true
    unsubscribe()
    if (frame !== undefined) cancelAnimationFrame(frame)
    if (targetTimer !== undefined) window.clearTimeout(targetTimer)
    if (quietTimer !== undefined) window.clearTimeout(quietTimer)
    window.removeEventListener(BOOT_SPLASH_REMOVED_EVENT, stop)
  }
  window.addEventListener(BOOT_SPLASH_REMOVED_EVENT, stop)

  const ready = (rect: DOMRect | null) => {
    stop()
    const target = rect && {
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
    }
    const boot = window.__multaqaBoot
    if (boot) boot.ready(target)
    else window.__multaqaBootPending = { target }
  }
  const findTarget = () =>
    document.querySelector<HTMLElement>('[data-boot-target]')?.getBoundingClientRect() ?? null
  const isVisible = (rect: DOMRect | null): rect is DOMRect =>
    Boolean(rect && rect.width > 0 && rect.height > 0)
  // Not the frame skeleton's logo: its guard may still redirect to sign-in.
  const layoutReady = () =>
    router.state.matches.some(
      ({ routeId, status }) => routeId === '/_authenticated' && status === 'success',
    )

  const poll = (timestamp: number) => {
    frame = undefined
    if (stopped) return
    if (!isBootSplashPresent()) {
      stop()
      return
    }
    if (!layoutReady()) {
      frame = requestAnimationFrame(poll)
      return
    }
    // The phone layout hides the rail (display: none): no flight is possible.
    const logo = document.querySelector<HTMLElement>('[data-boot-target]')
    if (logo?.checkVisibility?.() === false) {
      ready(null)
      return
    }
    const rect = findTarget()
    if (!isVisible(rect)) {
      frame = requestAnimationFrame(poll)
      return
    }

    if (targetTimer !== undefined) window.clearTimeout(targetTimer)
    targetTimer = undefined
    // The splash promotes the layers it will animate; the gate below waits out their raster.
    window.__multaqaBoot?.prepare?.()
    let previousTime = timestamp
    let smoothFrames = 0
    const gate = (timestamp: number) => {
      frame = undefined
      if (stopped) return
      smoothFrames = timestamp - previousTime < SMOOTH_FRAME_MS ? smoothFrames + 1 : 0
      previousTime = timestamp

      if (smoothFrames >= SMOOTH_FRAMES) {
        const restingRect = findTarget()
        if (isVisible(restingRect)) {
          ready(restingRect)
          return
        }
        resumePolling()
        return
      }
      frame = requestAnimationFrame(gate)
    }
    quietTimer = window.setTimeout(() => {
      const restingRect = findTarget()
      if (isVisible(restingRect)) ready(restingRect)
      else resumePolling()
    }, QUIET_WAIT_MS)
    frame = requestAnimationFrame(gate)
  }
  const resumePolling = () => {
    if (quietTimer !== undefined) window.clearTimeout(quietTimer)
    quietTimer = undefined
    if (targetDeadline !== undefined) {
      const remaining = targetDeadline - performance.now()
      if (remaining <= 0) {
        ready(null)
        return
      }
      targetTimer = window.setTimeout(() => ready(null), remaining)
    }
    frame = requestAnimationFrame(poll)
  }

  unsubscribeFromRouter = router.subscribe('onResolved', () => {
    if (stopped || !unsubscribeFromRouter) return
    unsubscribe()
    const targetExpected = router.state.matches.some(
      ({ routeId }) => routeId === '/_authenticated' || routeId.startsWith('/_authenticated/'),
    )
    if (!targetExpected) {
      if (frame !== undefined) cancelAnimationFrame(frame)
      // The screen may still be behind its pending fallback; fading now would
      // reveal a blank page and pop the screen in after the fade.
      const awaitScreen = () => {
        if (document.querySelector('[data-boot-pending]'))
          frame = requestAnimationFrame(awaitScreen)
        else ready(null)
      }
      targetTimer = window.setTimeout(() => ready(null), TARGET_WAIT_MS)
      frame = requestAnimationFrame(awaitScreen)
      return
    }
    // Still looking for the logo once the route has fully loaded: bound the wait.
    if (quietTimer !== undefined) return
    targetDeadline = performance.now() + TARGET_WAIT_MS
    targetTimer = window.setTimeout(() => ready(null), TARGET_WAIT_MS)
  })
  frame = requestAnimationFrame(poll)
}

/**
 * Resolves when the replayed dot has landed. Navigate then: the signed-in
 * app's first render blocks the main thread, and during the replay it froze
 * the sequence. The cap keeps a replay that never lands from holding sign-in.
 */
export function replayBootSplash(
  fromRect: BootTargetRect,
  router: RouteResolutionSource,
): Promise<void> {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return Promise.resolve()

  const boot = window.__multaqaBoot
  if (!boot) return Promise.resolve()

  const landed = boot.replay(fromRect)
  // Watch the route only from here: the sign-in screen resolves too, and
  // would hand the replay off as a signed-out route.
  return Promise.race([
    landed,
    new Promise<void>((resolve) => window.setTimeout(resolve, REPLAY_LAND_WAIT_MS)),
  ]).then(() => handoffAfterFirstRoute(router))
}
